import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';

// Step-by-step export instructions, shown from the info icon next to each bank
// and next to Payroll Files.
const BANK_INSTRUCTIONS = {
  PNC: {
    title: 'Instructions for PNC',
    sections: [
      {
        heading: 'Checking',
        steps: [
          'Select Account',
          'Select Transactions',
          'Select Show Filters',
          'Select Custom Date Range',
          'Enter the first day (start date) and the last day (end date) of the month of interest',
          'Select Apply Filters',
          'Select Export',
          'Select Excel (.csv).'
        ]
      },
      {
        heading: 'Credit',
        steps: [
          'Select Account',
          'Select Transactions',
          'Select Time Period of interest',
          'Select Export',
          'Select .csv'
        ]
      }
    ]
  },
  BOA: {
    title: 'Instructions for Bank of America',
    sections: [
      {
        heading: 'Checking',
        steps: [
          'Select Account',
          'Click on the Download link (just above Available balance)',
          'Select Transaction Period for month of interest',
          'Select File Type (Microsoft Excel Format)',
          'Select Download'
        ]
      },
      {
        heading: 'Credit',
        steps: [
          'Select Account (not the Cash Rewards account, but the one(s) below it)',
          'Click on the Download link (Just above Amount)',
          'Select the desired Transaction Period (do not select current transactions)',
          'Select File Type, Spreadsheet - Comma delimited text format',
          'Select Download transactions'
        ]
      }
    ]
  }
};

const PAYROLL_INSTRUCTIONS = {
  title: 'Instructions for Payroll',
  sections: [
    {
      heading: null,
      steps: [
        'Select Payroll from the bar on the left',
        'Click on "Completed" on the top menu bar',
        'Select the payrun of interest by clicking on the date. You can only select one at a time. For any given month, select the payruns that end in that same month.',
        'Click on the three dots at the top right',
        'Select Payroll Register',
        'Click Download, unselect PDF, and select CSV (no selection for Group By is required). Click on Download Report.'
      ]
    }
  ]
};

const getBankInstructions = (bankName) => {
  const name = String(bankName || '').toLowerCase();
  if (name.includes('pnc')) return BANK_INSTRUCTIONS.PNC;
  if (name.includes('bank of america') || name === 'boa' || name.includes('bofa')) return BANK_INSTRUCTIONS.BOA;
  return null;
};

const InfoIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const FinancialsImport = ({ userInfo }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedMonthNum, setSelectedMonthNum] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState({
    byMonth: {},
    selectedMonth: {
      statements: {},
      payroll: []
    }
  });
  const [bankAccounts, setBankAccounts] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [uploadingFiles, setUploadingFiles] = useState({});
  const [selectedFiles, setSelectedFiles] = useState({});
  const [payrollPeriods, setPayrollPeriods] = useState({});
  const [manuallyCompletedMonths, setManuallyCompletedMonths] = useState({});
  const [manuallyIncompletedMonths, setManuallyIncompletedMonths] = useState({});
  const [autoCompletedMonths, setAutoCompletedMonths] = useState({});
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewFile, setPreviewFile] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [warningMessage, setWarningMessage] = useState('');
  // Processing errors per upload row, kept until the next upload attempt on that row
  const [uploadErrors, setUploadErrors] = useState({});
  const [monthNotInCycle, setMonthNotInCycle] = useState(false);
  // Instructions shown in the modal ({ title, sections }), or null when closed.
  const [instructions, setInstructions] = useState(null);

  // Get current month and past 12 months for selection
  const getCurrentAndPast12Months = () => {
    const months = [];
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    for (let i = 0; i < 12; i++) {
      let monthIndex = currentMonth - i;
      let year = currentYear;

      if (monthIndex < 0) {
        monthIndex += 12;
        year -= 1;
      }

      const monthName = new Date(year, monthIndex, 1).toLocaleString('default', { month: 'long' });
      months.push({
        label: `${monthName} ${year}`,
        value: `${year}-${(monthIndex + 1).toString().padStart(2, '0')}`
      });
    }

    return months;
  };

  const months = getCurrentAndPast12Months();

  // Get available years (current year and past 2 years)
  const getAvailableYears = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    return [currentYear, currentYear - 1, currentYear - 2];
  };

  // Get month names
  const monthNames = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];

  const availableYears = getAvailableYears();

  // Initialize selected month from URL params or default to current month
  useEffect(() => {
    const monthParam = searchParams.get('month');
    if (monthParam) {
      // Allow any valid YYYY-MM format, but track if it's not in the cycle
      const isValidFormat = /^\d{4}-\d{2}$/.test(monthParam);
      if (isValidFormat) {
        setSelectedMonth(monthParam);
        const [year, monthNum] = monthParam.split('-');
        setSelectedYear(year);
        setSelectedMonthNum(monthNum);
        // Check if this month is in the available months list
        setMonthNotInCycle(!months.some(m => m.value === monthParam));
      } else if (months.length > 0) {
        setSelectedMonth(months[0].value);
        const [year, monthNum] = months[0].value.split('-');
        setSelectedYear(year);
        setSelectedMonthNum(monthNum);
        setMonthNotInCycle(false);
      }
    } else if (months.length > 0) {
      setSelectedMonth(months[0].value);
      const [year, monthNum] = months[0].value.split('-');
      setSelectedYear(year);
      setSelectedMonthNum(monthNum);
      setMonthNotInCycle(false);
    }
  }, [searchParams]);

  // Update URL when month changes
  const handleMonthChange = (monthYear) => {
    setSelectedMonth(monthYear);
    setSearchParams({ month: monthYear });
    setMonthNotInCycle(!months.some(m => m.value === monthYear));
  };

  // Handle separate year/month selection
  const handleYearChange = (year) => {
    setSelectedYear(year);
    const newMonthYear = `${year}-${selectedMonthNum}`;
    setSelectedMonth(newMonthYear);
    setSearchParams({ month: newMonthYear });
    setMonthNotInCycle(!months.some(m => m.value === newMonthYear));
  };

  const handleMonthNumChange = (monthNum) => {
    setSelectedMonthNum(monthNum);
    const newMonthYear = `${selectedYear}-${monthNum}`;
    setSelectedMonth(newMonthYear);
    setSearchParams({ month: newMonthYear });
    setMonthNotInCycle(!months.some(m => m.value === newMonthYear));
  };

  // Initialize payroll periods for months
  useEffect(() => {
    const initializePayrollPeriods = () => {
      if (Object.keys(payrollPeriods).length > 0) {
        return;
      }

      const initialPeriods = {};
      months.forEach(month => {
        initialPeriods[month.value] = [1, 2]; // Default to periods 1 and 2 for each month
      });
      setPayrollPeriods(initialPeriods);
    };

    initializePayrollPeriods();
  }, []);

  // Fetch data on mount
  useEffect(() => {
    fetchBankAccounts();
    fetchAllMonthsFiles();
    loadManualMonthStatus();
  }, []);

  // Update selected month files when selection changes
  useEffect(() => {
    if (selectedMonth && uploadedFiles.byMonth && uploadedFiles.byMonth[selectedMonth]) {
      setUploadedFiles(prevFiles => ({
        ...prevFiles,
        selectedMonth: {
          statements: { ...prevFiles.byMonth[selectedMonth].statements },
          payroll: prevFiles.byMonth[selectedMonth].payroll || []
        }
      }));
    }
  }, [selectedMonth, uploadedFiles.byMonth]);

  // Get company-specific file path prefix
  const getCompanyFilePath = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/files/company-file-path`, {
        method: 'GET',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      return data.companyMonthlyReportsPath;
    } catch (error) {
      console.error('Error getting company file path:', error);
      return 'monthly_reports';
    }
  };

  const fetchBankAccounts = async () => {
    try {
      // Use the same /company/banks endpoint as CompanyInfo page
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/company/banks`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        const banks = data.banks || [];
        
        // Transform flat array to nested object structure:
        // { "Mercury": [{ type: "Checking", lastFour: "1234", id: "..." }], ... }
        const bankAccountsMap = {};
        banks.forEach((bank, index) => {
          const bankName = bank.bankName;
          if (!bankName) return;
          
          if (!bankAccountsMap[bankName]) {
            bankAccountsMap[bankName] = [];
          }
          
          // Extract last 4 digits from account number
          const accountNumber = bank.accountNumber || '';
          const lastFour = accountNumber.slice(-4) || accountNumber;
          
          bankAccountsMap[bankName].push({
            id: `${bankName}-${index}`,
            type: bank.accountType || 'Checking',
            lastFour: lastFour
          });
        });
        
        setBankAccounts(bankAccountsMap);
      }
    } catch (error) {
      console.error('Error fetching bank accounts:', error);
    }
  };

  const fetchAllMonthsFiles = async () => {
    setIsLoading(true);

    try {
      const allFilesData = {
        byMonth: {},
        selectedMonth: {
          statements: {},
          payroll: []
        }
      };

      await Promise.all(months.map(async (month) => {
        const [year, monthNum] = month.value.split('-');

        try {
          const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/files/monthly-statements?year=${year}&month=${monthNum}`, {
            method: 'GET',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json'
            }
          });

          if (!response.ok) {
            console.error(`Error fetching files for ${month.label}: ${response.statusText}`);
            return;
          }

          const data = await response.json();

          allFilesData.byMonth[month.value] = {
            statements: {},
            payroll: []
          };

          (data.bankStatements || []).forEach(statement => {
            if (!allFilesData.byMonth[month.value].statements[statement.bank]) {
              allFilesData.byMonth[month.value].statements[statement.bank] = {};
            }

            allFilesData.byMonth[month.value].statements[statement.bank][statement.type] = {
              filename: statement.filename,
              documentName: statement.documentName,
              uploadDate: statement.uploadDate,
              id: statement.id,
              monthYear: month.value,
              monthLabel: month.label
            };
          });

          if (Array.isArray(data.payroll)) {
            allFilesData.byMonth[month.value].payroll = data.payroll.map(p => ({
              filename: p.filename,
              documentName: p.documentName,
              uploadDate: p.uploadDate,
              id: p.id,
              payrollPeriod: p.payrollPeriod || '1',
              monthYear: month.value,
              monthLabel: month.label
            }));
          } else if (data.payroll) {
            allFilesData.byMonth[month.value].payroll.push({
              filename: data.payroll.filename,
              documentName: data.payroll.documentName,
              uploadDate: data.payroll.uploadDate,
              id: data.payroll.id,
              payrollPeriod: data.payroll.payrollPeriod || '1',
              monthYear: month.value,
              monthLabel: month.label
            });
          }

          if (month.value === selectedMonth) {
            allFilesData.selectedMonth = {
              statements: { ...allFilesData.byMonth[month.value].statements },
              payroll: [...allFilesData.byMonth[month.value].payroll]
            };
          }
        } catch (error) {
          console.error(`Error fetching files for ${month.label}:`, error);
        }
      }));

      setUploadedFiles(allFilesData);
      return allFilesData;
    } catch (error) {
      console.error('Error fetching files for all months:', error);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const loadManualMonthStatus = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/month-status/month-completion-status`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();

      const completed = {};
      const incompleted = {};
      const autoCompleted = {};

      if (data.months) {
        Object.entries(data.months).forEach(([monthYear, status]) => {
          if (status === 'complete') {
            completed[monthYear] = true;
          } else if (status === 'incomplete') {
            incompleted[monthYear] = true;
          } else if (status === 'auto-complete') {
            autoCompleted[monthYear] = true;
          }
        });
      }

      setManuallyCompletedMonths(completed);
      setManuallyIncompletedMonths(incompleted);
      setAutoCompletedMonths(autoCompleted);
    } catch (error) {
      console.error('Error loading month status:', error);
    }
  };

  const isMonthComplete = (monthYear) => {
    if (manuallyCompletedMonths[monthYear]) {
      return true;
    }

    if (manuallyIncompletedMonths[monthYear]) {
      return false;
    }

    if (autoCompletedMonths[monthYear]) {
      return true;
    }

    if (!uploadedFiles.byMonth || !uploadedFiles.byMonth[monthYear]) {
      return false;
    }

    const monthData = uploadedFiles.byMonth[monthYear];
    let isComplete = true;

    // Check if all bank accounts have statements uploaded
    const bankNames = Object.keys(bankAccounts);
    if (bankNames.length === 0) {
      return false;
    }

    bankNames.forEach(bank => {
      const accounts = bankAccounts[bank] || [];
      if (accounts.length === 0) {
        isComplete = false;
        return;
      }

      accounts.forEach(account => {
        const accountKey = `${account.type}-${account.lastFour}`;
        if (!monthData.statements[bank] || !monthData.statements[bank][accountKey]) {
          isComplete = false;
        }
      });
    });

    if (!monthData.payroll || monthData.payroll.length < 2) {
      isComplete = false;
    }

    return isComplete;
  };

  // Check if a month has all required files uploaded and update backend status
  const checkAndUpdateAutoCompleteStatus = async (monthYear, filesData, bankAccountsData) => {
    // Skip if month is already manually completed/incompleted
    if (manuallyCompletedMonths[monthYear] || manuallyIncompletedMonths[monthYear]) {
      return;
    }

    const monthData = filesData?.byMonth?.[monthYear];
    if (!monthData) {
      return;
    }

    let isComplete = true;

    // Check if all bank accounts have statements uploaded
    const bankNames = Object.keys(bankAccountsData);
    if (bankNames.length === 0) {
      return;
    }

    bankNames.forEach(bank => {
      const accounts = bankAccountsData[bank] || [];
      if (accounts.length === 0) {
        isComplete = false;
        return;
      }

      accounts.forEach(account => {
        const accountKey = `${account.type}-${account.lastFour}`;
        if (!monthData.statements?.[bank]?.[accountKey]) {
          isComplete = false;
        }
      });
    });

    if (!monthData.payroll || monthData.payroll.length < 2) {
      isComplete = false;
    }

    // If complete and not already marked as auto-complete, save to backend
    if (isComplete && !autoCompletedMonths[monthYear]) {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/month-status/month-completion-status`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            monthYear,
            status: 'auto-complete'
          })
        });

        if (response.ok) {
          setAutoCompletedMonths(prev => ({
            ...prev,
            [monthYear]: true
          }));
        }
      } catch (error) {
        console.error('Error saving auto-complete status:', error);
      }
    }
  };

  // Sends the file to the import endpoint that parses it into transactions.
  // Returns the parsed response body, or null if the user cancelled at the duplicates prompt.
  // Throws with the server's message when the file couldn't be processed.
  const importStatementFile = async (endpoint, buildFormData) => {
    const post = (override) => fetch(`${process.env.REACT_APP_BACKEND_API_URL}${endpoint}`, {
      method: 'POST',
      credentials: 'include',
      body: buildFormData(override)
    });

    let response = await post(false);

    if (response.status === 409) {
      const data = await response.json().catch(() => ({}));
      const duplicates = data.duplicates || [];
      const count = Array.isArray(duplicates)
        ? duplicates.length
        : Object.values(duplicates).reduce((sum, list) => sum + (list?.length || 0), 0);
      const proceed = window.confirm(
        `${count} transaction(s) in this file were already imported. Import the remaining transactions and skip the duplicates?`
      );
      if (!proceed) return null;
      response = await post(true);
    }

    const text = await response.text();
    if (!response.ok) {
      throw new Error(text || response.statusText || `Request failed with status ${response.status}`);
    }
    try {
      return JSON.parse(text);
    } catch {
      return { message: text };
    }
  };

  // One-line summary of what an import created, e.g. "12 expenses, 3 deposits".
  const summarizeImport = (result) => {
    const stats = result?.statistics;
    const parts = [];
    if (stats) {
      const count = (value) => (typeof value === 'number' ? value : value?.imported || 0);
      if (count(stats.expense)) parts.push(`${count(stats.expense)} expense(s)`);
      if (count(stats.deposit)) parts.push(`${count(stats.deposit)} deposit(s)`);
      if (count(stats.payroll)) parts.push(`${count(stats.payroll)} payroll transaction(s)`);
      if (count(stats.rippling)) parts.push(`${count(stats.rippling)} payroll transaction(s)`);
      return parts.length ? `Imported ${parts.join(', ')}.` : 'No new transactions were imported.';
    }
    return result?.message || '';
  };

  // Uploads start as soon as a file is chosen, so there is no separate Upload click.
  const handleUploadFile = async (e, year, month, type, bank = null, payrollPeriod = null, chosenFile = null) => {
    if (e?.preventDefault) e.preventDefault();

    const rowId = bank ? `${year}-${month}-${type}-${bank}` : `${year}-${month}-payroll-${payrollPeriod}`;

    const file = chosenFile || selectedFiles[rowId];
    if (!file) {
      setErrorMessage('Please select a file to upload.');
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }

    setUploadingFiles(prev => ({ ...prev, [rowId]: true }));
    setUploadErrors(prev => {
      const updated = { ...prev };
      delete updated[rowId];
      return updated;
    });
    setErrorMessage('');
    setWarningMessage('');

    let importResult;
    try {
      // Process the file first so a statement that can't be read isn't saved as uploaded.
      if (type === 'payroll') {
        importResult = await importStatementFile('/payroll/import', () => {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('period', payrollPeriod);
          formData.append('year', year);
          formData.append('month', month);
          return formData;
        });
      } else {
        let bankIdentifier = bank;
        if (bank.toLowerCase().includes('mercury')) {
          bankIdentifier = 'mercury';
        } else if (bank.toLowerCase().includes('pnc')) {
          bankIdentifier = 'pnc';
        } else if (bank.toLowerCase().includes('bank of america') || bank.toLowerCase().includes('boa')) {
          bankIdentifier = 'boa';
        }

        const accountType = type.split('-')[0].toLowerCase();
        const lastFourDigits = type.split('-')[1] || '';

        let importEndpoint;
        if (accountType === 'debit' || accountType === 'checking') {
          importEndpoint = '/expense/import';
        } else if (accountType === 'credit') {
          importEndpoint = '/credit-card/import';
        } else if (accountType === 'savings') {
          importEndpoint = '/savings/import';
        } else {
          throw new Error(`Unknown account type: ${accountType}`);
        }

        importResult = await importStatementFile(importEndpoint, (override) => {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('bankType', bankIdentifier);
          formData.append('bankName', bank);
          formData.append('accountType', accountType);
          formData.append('lastFourDigits', lastFourDigits);
          if (override) formData.append('override', 'true');
          return formData;
        });
      }

      if (importResult === null) {
        setWarningMessage('Upload cancelled. No transactions were imported.');
        return;
      }

      const companyPath = await getCompanyFilePath();

      let filePath;
      let fileName = file.name;

      if (type === 'payroll') {
        filePath = `${companyPath}/${year}/${month}/payroll/period${payrollPeriod}/${fileName}`;
      } else {
        filePath = `${companyPath}/${year}/${month}/bank_statements/${bank}/${type}/${fileName}`;
      }

      // Get upload URL
      const uploadUrlResponse = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/files/generate-upload-url`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fileName: filePath,
          contentType: file.type || 'application/octet-stream',
          skipFolderName: true
        })
      });

      if (!uploadUrlResponse.ok) {
        throw new Error(`Failed to generate upload URL: ${await uploadUrlResponse.text()}`);
      }

      const { url } = await uploadUrlResponse.json();

      // Upload file to storage
      const uploadResponse = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type || 'application/octet-stream'
        },
        body: file
      });

      if (!uploadResponse.ok) {
        throw new Error(`Failed to upload file: ${uploadResponse.statusText}`);
      }

      // Register the uploaded file
      const registrationData = {
        fileName: filePath,
        originalName: fileName,
        fileType: type,
        year,
        month
      };

      if (type !== 'payroll') {
        registrationData.bank = bank;
      } else {
        registrationData.payrollPeriod = payrollPeriod;
      }

      const registrationResponse = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/files/register-monthly-statement`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(registrationData)
      });

      if (!registrationResponse.ok) {
        throw new Error(`Failed to register file: ${registrationResponse.statusText}`);
      }

      // Clear form data
      setSelectedFiles(prev => {
        const updated = {...prev};
        delete updated[rowId];
        return updated;
      });

      // Refresh file list and check for auto-complete
      const refreshedFiles = await fetchAllMonthsFiles();
      const monthYear = `${year}-${month}`;
      
      // Check if this upload completed the month
      if (refreshedFiles) {
        await checkAndUpdateAutoCompleteStatus(monthYear, refreshedFiles, bankAccounts);
      }

      setSuccessMessage(`File uploaded and processed. ${summarizeImport(importResult)}`.trim());
      setTimeout(() => setSuccessMessage(''), 6000);

      if (importResult?.skippedRows > 0) {
        setWarningMessage(`${file.name}: ${importResult.skippedRows} row(s) had an unreadable date or amount and were skipped. Please review the imported transactions.`);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      // Not auto-dismissed: a failed import is easy to miss otherwise
      if (importResult) {
        setErrorMessage(`${file.name} was processed (${summarizeImport(importResult)}) but the file itself could not be saved: ${error.message}`);
      } else {
        setErrorMessage(`${file.name} could not be processed: ${error.message}`);
        setUploadErrors(prev => ({ ...prev, [rowId]: error.message }));
      }
    } finally {
      setUploadingFiles(prev => {
        const updated = {...prev};
        delete updated[rowId];
        return updated;
      });
    }
  };

  const handleDeleteFile = async (file) => {
    if (!window.confirm('Are you sure you want to delete this file? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/files/delete-monthly-statement`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fileName: file.documentName
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to delete file: ${await response.text()}`);
      }

      await fetchAllMonthsFiles();
      setSuccessMessage('File deleted successfully.');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error deleting file:', error);
      setErrorMessage(`Failed to delete file: ${error.message}`);
      setTimeout(() => setErrorMessage(''), 5000);
    }
  };

  const handlePreviewFile = async (file) => {
    try {
      setPreviewFile(file);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/files/generate-preview-url`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ fileName: file.documentName, skipFolderName: true })
      });

      if (!response.ok) {
        throw new Error(`Failed to generate preview URL: ${response.statusText}`);
      }

      const { url } = await response.json();
      setPreviewUrl(url);
    } catch (error) {
      console.error('Error previewing file:', error);
      setErrorMessage('Error previewing file. Please try again.');
      setTimeout(() => setErrorMessage(''), 3000);
    }
  };

  const handleDownloadFile = async (file) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/files/generate-download-url`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ fileName: file.documentName, skipFolderName: true })
      });

      if (!response.ok) {
        throw new Error(`Failed to generate download URL: ${response.statusText}`);
      }

      const { url } = await response.json();

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.filename || 'downloaded-file');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading file:', error);
      setErrorMessage('Error downloading file. Please try again.');
      setTimeout(() => setErrorMessage(''), 3000);
    }
  };

  const closePreview = () => {
    setPreviewUrl('');
    setPreviewFile(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const addPayrollPeriod = (monthYear) => {
    const uploadedPeriods = (uploadedFiles.byMonth?.[monthYear]?.payroll || [])
      .map(p => parseInt(p.payrollPeriod, 10))
      .filter(n => !Number.isNaN(n));
    setPayrollPeriods(prev => {
      const currentPeriods = Array.from(new Set([...(prev[monthYear] || [1, 2]), ...uploadedPeriods]));
      const maxPeriod = Math.max(...currentPeriods);
      return {
        ...prev,
        [monthYear]: [...currentPeriods, maxPeriod + 1]
      };
    });
  };

  const removePayrollPeriod = (monthYear, periodToRemove) => {
    if (periodToRemove <= 2) {
      setErrorMessage("Periods 1 and 2 cannot be removed.");
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }

    const hasFile = uploadedFiles.byMonth?.[monthYear]?.payroll?.some(
      p => p.payrollPeriod === periodToRemove.toString()
    );

    let confirmMessage = `Are you sure you want to remove Pay Period ${periodToRemove}?`;
    if (hasFile) {
      confirmMessage = `Pay Period ${periodToRemove} has an uploaded file. Removing this period will not delete the file, but it will no longer be visible in the interface. Are you sure you want to continue?`;
    }

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setPayrollPeriods(prev => {
      const currentPeriods = prev[monthYear] || [1, 2];

      if (currentPeriods.length <= 2) {
        return prev;
      }

      return {
        ...prev,
        [monthYear]: currentPeriods.filter(period => period !== periodToRemove)
      };
    });
  };

  const markMonthAsComplete = async (monthYear) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/month-status/month-completion-status`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          monthYear,
          status: 'complete'
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      setManuallyCompletedMonths(prev => ({
        ...prev,
        [monthYear]: true
      }));

      setManuallyIncompletedMonths(prev => {
        const updated = { ...prev };
        delete updated[monthYear];
        return updated;
      });

      setSuccessMessage(`${getMonthLabel(monthYear)} has been marked as complete.`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error marking month as complete:', error);
      setErrorMessage('Failed to update month status. Please try again.');
      setTimeout(() => setErrorMessage(''), 5000);
    }
  };

  const markMonthAsIncomplete = async (monthYear) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/month-status/month-completion-status`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          monthYear,
          status: 'incomplete'
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      setManuallyIncompletedMonths(prev => ({
        ...prev,
        [monthYear]: true
      }));

      setManuallyCompletedMonths(prev => {
        const updated = { ...prev };
        delete updated[monthYear];
        return updated;
      });

      setSuccessMessage(`${getMonthLabel(monthYear)} has been marked as incomplete.`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error marking month as incomplete:', error);
      setErrorMessage('Failed to update month status. Please try again.');
      setTimeout(() => setErrorMessage(''), 5000);
    }
  };

  const getMonthLabel = (monthYear) => {
    const month = months.find(m => m.value === monthYear);
    return month ? month.label : monthYear;
  };

  const monthComplete = isMonthComplete(selectedMonth);
  const currentMonthData = uploadedFiles.byMonth?.[selectedMonth] || { statements: {}, payroll: [] };
  const bankNames = Object.keys(bankAccounts);

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <span className="ml-3 text-gray-600">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="card p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Financials Import</h2>
        <p className="text-gray-600">Upload and manage monthly bank statements and payroll files</p>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
          {successMessage}
        </div>
      )}
      {errorMessage && (
        <div className="flex items-start justify-between gap-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          <span>{errorMessage}</span>
          <button className="text-red-500 hover:text-red-800 text-lg leading-none" onClick={() => setErrorMessage('')} title="Dismiss">×</button>
        </div>
      )}
      {warningMessage && (
        <div className="flex items-start justify-between gap-4 p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-800">
          <span>{warningMessage}</span>
          <button className="text-amber-600 hover:text-amber-900 text-lg leading-none" onClick={() => setWarningMessage('')} title="Dismiss">×</button>
        </div>
      )}

      {/* Month Selector */}
      <div className="card p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-end gap-4">
            <div>
              <label htmlFor="year-select" className="block text-sm font-medium text-gray-700 mb-1">
                Year
              </label>
              <select
                id="year-select"
                value={selectedYear}
                onChange={(e) => handleYearChange(e.target.value)}
                className="input-field min-w-[120px]"
              >
                {availableYears.map(year => (
                  <option key={year} value={String(year)}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="month-select" className="block text-sm font-medium text-gray-700 mb-1">
                Month
              </label>
              <select
                id="month-select"
                value={selectedMonthNum}
                onChange={(e) => handleMonthNumChange(e.target.value)}
                className="input-field min-w-[150px]"
              >
                {monthNames.map(month => (
                  <option key={month.value} value={month.value}>
                    {month.label} {isMonthComplete(`${selectedYear}-${month.value}`) ? '✓' : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {monthComplete ? (
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-100 text-green-700 text-sm font-medium">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                </svg>
                Complete
              </span>
            ) : (
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 text-sm font-medium">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                </svg>
                Incomplete
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Warning when month is not in cycle */}
      {monthNotInCycle && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-amber-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
            </svg>
            <div>
              <p className="text-amber-800 font-medium">This month is not part of your monthly tasks cycle</p>
              <p className="text-amber-700 text-sm">Select a month within your active cycle to upload files.</p>
            </div>
          </div>
        </div>
      )}

      {/* Upload Section for Selected Month - Only show if month is in cycle */}
      {!monthNotInCycle && (
      <>
        <div className="card p-6">
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-lg font-semibold text-gray-900">
              Files for {getMonthLabel(selectedMonth)}
            </h3>
            <div className="flex gap-2">
              {monthComplete ? (
                <button
                  className="btn-secondary"
                  onClick={() => markMonthAsIncomplete(selectedMonth)}
                >
                  Mark as Incomplete
                </button>
              ) : (
                <button
                  className="btn-primary"
                  onClick={() => markMonthAsComplete(selectedMonth)}
                >
                  Mark as Complete
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Bank Statements Card */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-base font-medium text-gray-900">Bank Statements</h4>
            <div className="flex items-center gap-3">
              <Link
                to="/company-info"
                className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit Banks
              </Link>
            </div>
          </div>

          {bankNames.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-gray-500">No bank accounts configured.</p>
              <p className="text-sm text-gray-400 mt-1">Please add bank accounts in Company Info to upload statements.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {bankNames.map(bank => {
                const bankData = currentMonthData.statements?.[bank] || {};
                const accounts = bankAccounts[bank] || [];

                return (
                  <div key={bank} className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <h5 className="text-sm font-semibold text-gray-800">{bank}</h5>
                      <button
                        type="button"
                        onClick={() => {
                          const bankInstructions = getBankInstructions(bank);
                          if (bankInstructions) {
                            setInstructions(bankInstructions);
                          } else if (process.env.REACT_APP_BANK_STATEMENT_INSTRUCTIONS_URL) {
                            window.open(process.env.REACT_APP_BANK_STATEMENT_INSTRUCTIONS_URL, '_blank');
                          }
                        }}
                        className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700"
                        title={`How to export ${bank} statements`}
                      >
                        <InfoIcon />
                        Instructions
                      </button>
                    </div>

                    {accounts.length === 0 ? (
                      <p className="text-gray-500 text-sm italic">
                        No accounts configured for {bank}.
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {accounts.map(account => {
                          const accountKey = `${account.type}-${account.lastFour}`;
                          const accountData = bankData[accountKey];
                          const rowId = `${selectedMonth}-${accountKey}-${bank}`;

                          return (
                            <div key={account.id || accountKey} className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg">
                              <div className="flex-shrink-0 w-48">
                                <span className="text-sm font-medium text-gray-900">
                                  {account.type.charAt(0).toUpperCase() + account.type.slice(1)} (*{account.lastFour})
                                </span>
                              </div>
                              <div className="flex-1 ml-4">
                                {accountData ? (
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <span className="text-sm font-medium text-gray-900">{accountData.filename}</span>
                                      <span className="text-xs text-gray-500 ml-2">
                                        Uploaded: {formatDate(accountData.uploadDate)}
                                      </span>
                                    </div>
                                    <div className="flex gap-2">
                                      <button className="btn-secondary text-xs" onClick={() => handlePreviewFile(accountData)}>Preview</button>
                                      <button className="btn-primary text-xs" onClick={() => handleDownloadFile(accountData)}>Download</button>
                                      <button className="text-xs px-2 py-1 text-red-700 bg-red-50 border border-red-200 rounded hover:bg-red-100" onClick={() => handleDeleteFile(accountData)}>Delete</button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-3">
                                    <span className="text-sm text-amber-600 italic">Not uploaded</span>
                                    <input
                                      type="file"
                                      id={`upload-${rowId}`}
                                      className="text-sm text-gray-900 bg-white border border-gray-300 rounded p-1"
                                      disabled={uploadingFiles[rowId]}
                                      onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (!file) return;
                                        const [year, monthNum] = selectedMonth.split('-');
                                        handleUploadFile(null, year, monthNum, accountKey, bank, null, file);
                                        e.target.value = '';
                                      }}
                                    />
                                    {uploadingFiles[rowId] && (
                                      <span className="text-xs text-gray-500">Uploading...</span>
                                    )}
                                  </div>
                                )}
                                {uploadErrors[rowId] && (
                                  <p className="mt-2 text-xs text-red-600">Not processed: {uploadErrors[rowId]}</p>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Payroll Files Card */}
        <div className="card p-6">
          <div className="flex items-center gap-2 mb-4">
            <h4 className="text-base font-medium text-gray-900">Payroll Files</h4>
            <button
              type="button"
              onClick={() => setInstructions(PAYROLL_INSTRUCTIONS)}
              className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700"
              title="How to export payroll registers"
            >
              <InfoIcon />
              Instructions
            </button>
          </div>

          <div className="space-y-4">
            {(() => {
              // Periods with an uploaded file always show, even if this session's
              // period list (which isn't saved) doesn't include them.
              const uploadedPeriods = (currentMonthData.payroll || [])
                .map(p => parseInt(p.payrollPeriod, 10))
                .filter(n => !Number.isNaN(n));
              return Array.from(new Set([...(payrollPeriods[selectedMonth] || [1, 2]), ...uploadedPeriods]))
                .sort((a, b) => a - b);
            })().map((period) => {
              const payrollFile = currentMonthData.payroll?.find(
                p => p.payrollPeriod === period.toString()
              );
              const rowId = `${selectedMonth}-payroll-${period}`;

              return (
                <div key={`payroll-${period}`} className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-gray-900">Pay Period {period}</span>
                    {period > 2 && (
                      <button
                        className="text-xs text-red-600 hover:text-red-800"
                        onClick={() => removePayrollPeriod(selectedMonth, period)}
                        title="Remove this payroll period"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <div className="flex-1 ml-4">
                    {payrollFile ? (
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-sm font-medium text-gray-900">{payrollFile.filename}</span>
                          <span className="text-xs text-gray-500 ml-2">
                            Uploaded: {formatDate(payrollFile.uploadDate)}
                          </span>
                        </div>
                        <div className="flex gap-2">
                          <button className="btn-secondary text-xs" onClick={() => handlePreviewFile(payrollFile)}>Preview</button>
                          <button className="btn-primary text-xs" onClick={() => handleDownloadFile(payrollFile)}>Download</button>
                          <button className="text-xs px-2 py-1 text-red-700 bg-red-50 border border-red-200 rounded hover:bg-red-100" onClick={() => handleDeleteFile(payrollFile)}>Delete</button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-amber-600 italic">Not uploaded</span>
                        <input
                          type="file"
                          id={`upload-${rowId}`}
                          className="text-sm text-gray-900 bg-white border border-gray-300 rounded p-1"
                          disabled={uploadingFiles[rowId]}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            const [year, monthNum] = selectedMonth.split('-');
                            handleUploadFile(null, year, monthNum, 'payroll', null, period, file);
                            e.target.value = '';
                          }}
                        />
                        {uploadingFiles[rowId] && (
                          <span className="text-xs text-gray-500">Uploading...</span>
                        )}
                      </div>
                    )}
                    {uploadErrors[rowId] && (
                      <p className="mt-2 text-xs text-red-600">Not processed: {uploadErrors[rowId]}</p>
                    )}
                  </div>
                </div>
              );
            })}

            <button
              className="btn-secondary w-full"
              onClick={() => addPayrollPeriod(selectedMonth)}
            >
              + Add Payroll Period
            </button>
          </div>
        </div>
      </>
      )}

      {/* Export instructions modal */}
      {instructions && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-5"
          onClick={() => setInstructions(null)}
        >
          <div className="bg-white rounded-lg w-full max-w-lg max-h-[85vh] overflow-auto p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{instructions.title}</h3>
              <button className="text-gray-500 hover:text-gray-800 text-xl" onClick={() => setInstructions(null)}>×</button>
            </div>
            {instructions.sections.map((section, i) => (
              <div key={i} className="mb-4">
                {section.heading && <h4 className="text-sm font-semibold text-gray-800 mb-1">{section.heading}</h4>}
                <ol className="list-decimal pl-5 space-y-1 text-sm text-gray-700">
                  {section.steps.map((step, j) => <li key={j}>{step}</li>)}
                </ol>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* File Preview Modal */}
      {previewUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-5">
          <div className="bg-white rounded-lg w-full max-w-6xl h-[90vh] flex flex-col overflow-hidden shadow-xl">
            <div className="flex items-center justify-between p-4 bg-gray-100 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 truncate">{previewFile?.filename}</h3>
              <button className="text-gray-600 hover:text-gray-900 text-2xl" onClick={closePreview}>x</button>
            </div>
            <div className="flex-1 overflow-auto">
              {previewUrl.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                <img src={previewUrl} alt="File Preview" className="object-contain w-full h-full" />
              ) : (
                <iframe src={previewUrl} title="Document Preview" className="w-full h-full" />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancialsImport;
